"use strict";

/**
 * BunsenModal is a modal native to Bunsen and does not on jQuery plugins or external css files.
 * i.e. it can be used exactly the same way in .com and adminv2 without modifications to the
 * entry .js or base .twig file. Supports an extended subset of the modalMap interface from
 * adminv2's popModal view.
 *
 * @param {Object}        modalConfig         - modal config object. Provide as the value for your modalMap's attributes
 * @param {String}        modalConfig.title   - title of modal
 * @param {Function}      modalConfig.options - a function that returns an options map for the content view constructor
 * @param {Object}        modalConfig.options - an options map that will be passed to the modal content view constructor (see documentation for BunsenModal.prototype.initialize)
 * @param {Constructor}   modalConfig.View    - a Backbone View constructor that will be used to build the modal content
 * @param {Backbone.View} modalConfig.view    - a Backbone View instance (if provided, modalConfig.options will be ignored)
 * @example
 * Use with your view like this:
 *
 *    var extendCompose = require('bunsen/libraries/extendCompose');
 *    var Backbone = require('backbone');
 *
 *    var MyView = extendCompose.call(Backbone.View, [BunsenModal.mixin, {
 *        el: $("<div><div data-modal='myModal'>click to open my modal</div></div>"),
 *        modalMap: {
 *            myModal: {
 *                reuse: true, // set to false if the modal and content view is to be re-created every time it is opened
 *                title: "This is the modal's title",
 *                options: function () {
 *                    // return an options map to be passed to View constructor
 *                },
 *                View: MyModalContentView
 *            }
 *        }
 *    }]);
 */

require('./BunsenModal-style.scss');
var template = require('./BunsenModal-template').template;
var PresentationManager = require('bunsen/helpers/PresentationManager');
var normalizeToView = require('bunsen/helpers/normalizeToView');
var Floater = require('bunsen/components/Floater/Floater-view');
var ButtonView = require('dibs-button-view');
var $ = require('dibs-jquery-shim');
var _ = require('underscore');
var bind = require('b3nd');
var newsy = require('newsy');
var Backbone = require('dibs-backbone');
var messageMixin = require('dibs-alert-view/messageMixin');
var mobileCheck = require('bunsen/helpers/mobileCheck');
var BunsenModal;
var popModal;
var $window;
var $body;

var getButtonsAlignment = function (buttonsAlignment) {
    return _.contains(['center', 'right'], buttonsAlignment) ? buttonsAlignment : 'left';
};

popModal = function (modalName) {
    var modal = new PresentationManager({
        configGroup: 'modalMap',
        parentView: this,
        viewConfigKey: modalName
    }).getView(newsy.getConstructor({
        key: 'BunsenModal',
        defaultConstructor: BunsenModal
    }));

    modal.show();
    return modal;
};
BunsenModal = module.exports = Floater.extend([messageMixin, {
    animated: true,
    position: {
        my: "center",
        at: "center",
        of: window
    },
    template: {
        src: template,
        useInnerElement: true
    },
    events: {
        'transitionend' : function (e) {
            e.stopPropagation();
        },
        'keydown .bunsen-modal-body' : function (e) { // TODO: get this working. It's not handling events as expected
            if (27 !== e.keyCode) { // escape key
                return;
            }

            this.hide();
        },
        'click .bunsen-modal-close' : 'hide',
        'click .bunsen-modal-scrim' : 'hide'
    },
    /**
     * @param {Backbone.View} options.content - content view for the modal. `el` will be appended to the modal's content area
     * @param {String} options.content - Markup to be parsed with jQuery and used as content for the modal
     * @param {DOM Element} options.content - Dom element to be appended to the content area of the modal
     * @param {jQuery} options.content - jQuery element to whose first element will be appended to the content area of the modal
     * @param {Boolean} options.manageValidButtons - disables all buttons if the contentview's model becomes invalid.
     * @param {Boolean} options.nestedEvents - listens to nested events from associated model when checking for model changes.
     * @param {Boolean} options.buttonsAlignment - set the buttons alignment to center/left/right (default: left). This value is relayed to ButtonView as options.prependLoading so the loading indicator will appear to the left of the right/center-justified buttons.
     */
    initialize: function (options) {
        var view;
        var contentModel;
        var buttonView;
        var manageButtonStates;
        $window = $(window);
        $body = $('body');
        options = _.extend({
            width: undefined,
            parentView: this,
            css: undefined,
            buttonsAlignment: 'left',
            showClose: true,
            title: undefined,
            titleHTML: undefined,
            buttons: undefined,
            manageValidButtons: false,
            manageButtonsValidator: undefined,
            nestedEvents: false,
            content: {  // content for the modal, it goes through jquery.append, so it can be stright HTML or an element.
                template: _.template("")
            }
        }, options);
        this._bodyProps = {};
        this._buttonsAlignment = getButtonsAlignment(options.buttonsAlignment);
        this._buttons = options.buttons;
        this._buttonAutoClose = options.buttonAutoClose;
        this._classNames = _.extend({
            modal: '',
            body: '',
            scrim: '',
            header: '',
            content: '',
            buttons: '',
            close: ''
        }, this.classNames, options.classNames);

        if (_.isFunction(options.content)) {
            options.content = options.content.call(options.parentView);
        }
        view = normalizeToView(_.pick(options, 'content', 'Constructor'));
        BunsenModal.__super__.initialize.apply(this, arguments);
        contentModel = _.result(view, 'model');
        buttonView = this.getSubView('buttons');
        if (buttonView) {
            buttonView.defaultContext = view;
        }
        if (options.manageValidButtons && buttonView && contentModel) {
            manageButtonStates = function () {
                var isValid = !_.result(contentModel, 'validate');
                if (_.isFunction(options.manageButtonsValidator)) {
                    isValid = !options.manageButtonsValidator.call(view);
                }
                _.result(buttonView, isValid ? 'reEnableButtons' : 'disableButtons');
            };
            contentModel.on('change', manageButtonStates);
            if (options.nestedEvents) {
                contentModel.on('nested-change', manageButtonStates);
            }
            manageButtonStates();
        }
        this.$(".bunsen-modal-content").append(view.el);
        this.$modal = this.$(".bunsen-modal-body");
        if (options.css) {
            this.$modal.css(options.css);
        }
        if (options.overflowScroll) {
            this.$modal.addClass('overflow-scroll');
        }
        if (options.title && options.titleHTML) {
            throw new Error('options.title and options.titleHTML were both defined, this will cause undefined behavior.');
        }
        this.model = new Backbone.Model(_.pick(options, 'title', 'titleHTML', 'showClose'));
        _.bindAll(this, 'center');
        bind(this);
        return options;
    },
    getMainElement: function () {
        return this.$modal.get(0);
    },
    show__: function () {
        $window.on('resize', this.center);
        this.disableScrolling();
    },
    hide__: function () {
        $window.off('resize', this.center);
        this.enableScrolling();
    },
    close: function () { // TODO: remove all uses of this method
        console.warn("BunsenModal's close method is deprecated. Use .hide() instead");
        return this.hide.apply(this, arguments);
    },
    open: function () { // TODO: remove all uses of this method
        console.warn("BunsenModal's open method is deprecated. Use .show() instead");
        return this.show.apply(this, arguments);
    },
    disableScrolling: function () {
        var $mainEl = $(this.getMainElement());
        var height;
        var winHeight;
        var top;

        this._bodyProps.position = $body.css('position');

        $body.addClass('modal-open');

        if (mobileCheck.any()) {
            $body.css({position: 'static'});

            height = $mainEl.height();
            winHeight = $window.height();
            top = (winHeight - height) / 2;
            if (top < 0) {
                top = 0;
                $mainEl.height('100%');
                $mainEl.css('overflow', 'scroll');
            }

            $mainEl.parent().css('position', 'fixed');
            if (!this.isCenteringDisabled()) {
                $mainEl.css('top', top);
            }
        }
    },
    enableScrolling: function () {
        $body.removeClass('modal-open');
        $body.css({position: this._bodyProps.position});
    },
    updateHeader: function (title) {
        this.model.set('title', title);
    },
    templateVars__ : function (obj) {
        return _.extend({
            buttonsAlignment: this._buttonsAlignment,
            classNames: this._classNames
        }, obj);
    },
    scope : {
        buttons: function () {
            if (!this._buttons) {
                return false;
            }
            var buttonView = new ButtonView({
                btnTmpl: this.btnTmpl,
                prependLoading: this._buttonsAlignment !== 'left',
                buttons: this._buttons
            }).render();
            if (this._buttonAutoClose) {
                buttonView.on('buttonClick', function (btnConfig, state) {
                    if ('rejected' !== state) {
                        this.hide({button: btnConfig});
                    }
                }, this);
            }
            return buttonView;
        }
    }
}], {
    newsy: 'BunsenModal',
    mixin: { // intended for use with ExtendCompose
        popModal: popModal,
        events__: {
            'click': PresentationManager.buildActivationEventHandler({
                dataAttribute: 'modal',
                handleGotKey: popModal
            })
        },
        modalMap__: {}
    }
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/components/BunsenModal/BunsenModal-view.js
// module id = 690
// module chunks = 4 6 7 8 9 13 17